import React from "react";

function shade(hex, amt) {
  try {
    let c = hex.replace("#", "");
    if (c.length === 3) c = c.split("").map((x) => x + x).join("");
    const num = parseInt(c, 16);
    let r = Math.max(0, Math.min(255, (num >> 16) + amt));
    let g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amt));
    let b = Math.max(0, Math.min(255, (num & 0xff) + amt));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  } catch {
    return hex;
  }
}

// index 0 = launch pad, last = galaxy goal, middle = 1 of 10 planet shapes.
// Every body is tinted with the project's `color`.
export default function Planet({ index, total, color = "#3B82F6", size = 64, planet = 0 }) {
  const isStart = index === 0;
  const isGoal = index === total - 1;
  const uid = `pl-${index}-${Math.round(size)}-${planet}`;
  const light = shade(color, 55);
  const dark = shade(color, -35);
  const wrap = (children) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">{children}</svg>
  );

  if (isStart) {
    return wrap(
      <>
        <ellipse cx="32" cy="56" rx="22" ry="5" fill="#000" opacity="0.35" />
        <path d="M12 50 L52 50 L46 40 L18 40 Z" fill={dark} />
        <rect x="14" y="49" width="36" height="4" rx="2" fill={light} />
        <rect x="22" y="18" width="4" height="24" fill="#94A3B8" />
        <rect x="22" y="22" width="14" height="3" fill="#94A3B8" />
        <rect x="22" y="30" width="14" height="3" fill="#94A3B8" />
        <rect x="22" y="38" width="14" height="3" fill="#94A3B8" />
        <rect x="38" y="14" width="2" height="14" fill="#CBD5E1" />
        <path d="M40 14 L50 17 L40 20 Z" fill={color} />
        <circle cx="40" cy="48" r="4" fill="#E2E8F0" opacity="0.5" />
      </>
    );
  }

  if (isGoal) {
    return wrap(
      <>
        <defs>
          <radialGradient id={uid} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fff" />
            <stop offset="35%" stopColor={light} />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </radialGradient>
        </defs>
        <g className="galaxy-spin" style={{ transformOrigin: "32px 32px" }}>
          <ellipse cx="32" cy="32" rx="28" ry="12" fill={color} opacity="0.18" transform="rotate(20 32 32)" />
          {Array.from({ length: 16 }).map((_, i) => {
            const a = (i / 16) * Math.PI * 4;
            const r = 6 + i * 1.4;
            return <circle key={i} cx={32 + Math.cos(a) * r} cy={32 + Math.sin(a) * r * 0.55} r={1.6} fill={i % 3 === 0 ? "#fff" : light} />;
          })}
        </g>
        <circle cx="32" cy="32" r="12" fill={`url(#${uid})`} />
        <circle cx="32" cy="32" r="4" fill="#fff" />
      </>
    );
  }

  const v = ((planet % 10) + 10) % 10;
  const body = <circle cx="32" cy="32" r="16" fill={color} />;
  const shine = <circle cx="26" cy="27" r="4" fill="#fff" opacity="0.18" />;

  const variants = {
    0: (
      <>
        <ellipse cx="32" cy="34" rx="27" ry="8" fill={light} opacity="0.35" transform="rotate(-18 32 34)" />
        {body}{shine}
        <ellipse cx="32" cy="34" rx="27" ry="8" fill="none" stroke={light} strokeWidth="2.5" transform="rotate(-18 32 34)" />
      </>
    ),
    1: (
      <>{body}
        <circle cx="26" cy="27" r="4" fill={dark} opacity="0.6" />
        <circle cx="39" cy="35" r="5" fill={dark} opacity="0.6" />
        <circle cx="30" cy="41" r="3" fill={dark} opacity="0.6" />{shine}
      </>
    ),
    2: (
      <>
        <defs><clipPath id={uid}><circle cx="32" cy="32" r="16" /></clipPath></defs>
        {body}
        <g clipPath={`url(#${uid})`}>
          <rect x="16" y="22" width="32" height="4" fill={light} opacity="0.7" />
          <rect x="16" y="30" width="32" height="5" fill={dark} opacity="0.6" />
          <rect x="16" y="38" width="32" height="4" fill={light} opacity="0.7" />
        </g>{shine}
      </>
    ),
    3: (
      <>
        <circle cx="30" cy="34" r="15" fill={color} />{shine}
        <circle cx="50" cy="18" r="5" fill={light} />
      </>
    ),
    4: (
      <>{body}
        <path d="M18 32 Q32 24 46 32 Q32 40 18 32 Z" fill={light} opacity="0.5" />{shine}
      </>
    ),
    5: (
      <>{body}
        <circle cx="32" cy="32" r="16" fill="none" stroke={light} strokeWidth="2" opacity="0.6" />
        <circle cx="32" cy="32" r="9" fill="none" stroke={dark} strokeWidth="2" opacity="0.6" />{shine}
      </>
    ),
    6: (
      <>
        <ellipse cx="32" cy="32" rx="28" ry="7" fill={light} opacity="0.4" />
        <ellipse cx="32" cy="32" rx="28" ry="7" fill="none" stroke={light} strokeWidth="2" />
        {body}{shine}
      </>
    ),
    7: (
      <>{body}
        {Array.from({ length: 7 }).map((_, i) => (
          <circle key={i} cx={22 + (i % 4) * 6} cy={26 + Math.floor(i / 4) * 8} r={1.6} fill={light} />
        ))}{shine}
      </>
    ),
    8: (
      <>
        <circle cx="32" cy="32" r="16" fill={dark} />
        <path d="M16 32 A16 16 0 0 1 48 32 Z" fill={light} opacity="0.5" />{shine}
      </>
    ),
    9: (
      <>{body}
        <ellipse cx="32" cy="24" rx="10" ry="4" fill={light} opacity="0.55" />
        <ellipse cx="32" cy="40" rx="12" ry="4" fill={dark} opacity="0.5" />{shine}
      </>
    ),
  };

  return wrap(variants[v]);
}
